/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import { Maybe, Nothing } from '@codegrade/maybe';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
class BaseMaybeMap {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _map;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(values) {
        if (values instanceof Map) {
            this._map = values;
        }
        else {
            this._map = new Map(values ?? []);
        }
        this.freeze();
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    freeze() {
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get(...keys) {
        for (const key of keys) {
            if (this._map.has(key)) {
                return Maybe.of(this._map.get(key));
            }
        }
        return Nothing;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    forEach(callbackfn, thisArg) {
        this._map.forEach(callbackfn, thisArg);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get size() {
        return this._map.size;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    entries() {
        return this._map.entries();
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    keys() {
        return this._map.keys();
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    values() {
        return this._map.values();
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    [Symbol.iterator]() {
        return this._map[Symbol.iterator]();
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    map(fun) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        const m = new MaybeMap();
        for (const [key, val] of this) {
            m.set(key, fun(val, key));
        }
        return m;
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class ReadonlyMaybeMap extends BaseMaybeMap {
    /**
     * Combine multiple readonly maps into a single one.
     *
     * @param maps The maps you want to combine.
     * @param combine The function called with the two values, it should combine them into a single
     * one.
     * @returns A freshly made `ReadonlyMaybeMap`.
     */
    static combine(maps, combine) {
        const res = new Map();
        for (const map of maps) {
            // eslint-disable-next-line no-underscore-dangle
            for (const [key, value] of map._map) {
                let newVal = value;
                if (res.has(key)) {
                    newVal = combine(res.get(key), value);
                }
                res.set(key, newVal);
            }
        }
        return new ReadonlyMaybeMap(res);
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class MaybeMap extends ReadonlyMaybeMap {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    set(key, value) {
        this._map.set(key, value);
        return value;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    delete(key) {
        return this._map.delete(key);
    }
    /**
     * Get the value at the given key and delete it from the map.
     *
     * @param key The key to get.
     * @returns The value at the requested key, if it exists.
     */
    getAndDelete(key) {
        const val = this.get(key);
        this.delete(key);
        return val;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    clear() {
        this._map.clear();
    }
    /**
     * Get an item from the map, or insert it if it doesn't exist yet.
     *
     * @param key The key to insert with.
     * @param getValue The function that should create the value if it doesn't exist yet.
     * @returns The found or just created value.
     */
    getOrInsertWith(key, getValue) {
        return this.get(key).caseOf({
            Just: v => v,
            Nothing: () => {
                const val = getValue();
                this.set(key, val);
                return val;
            },
        });
    }
}
